/**
 * Privileged-session detection for the storefront shopper swap.
 *
 * The storefront shopper-login path POSTs `/auth/getToken/<shopperId>` and
 * swaps the current session to a Shopper token, overwriting the shared
 * `localStorage["token"]`. Any back-office user -- a Super Admin,
 * Distributor, Distributor Manager, Client Admin, ... -- or a user
 * impersonating a distributor must NEVER be swapped to a shopper: doing so
 * tears down their session and their subsequent admin/impersonation API
 * calls 4xx. Such users view a store in the existing preview/read path
 * instead (the same `isStorefrontPreview` skip). THIS is the fix for the
 * reported cross-tab / own-store-preview logout bug.
 *
 * The rule is an INVERSION: the storefront skips the swap for EVERYONE
 * except a genuine Shopper. Only two kinds of session are swap-eligible:
 *   - a real Shopper session (roleName === 'Shopper'), and
 *   - a truly anonymous / logged-out visitor (NO roleName at all) -- they
 *     legitimately need the shopper swap to start shopping.
 * Every other logged-in role is privileged and must NOT swap.
 *
 * HOW it works. `roleName` and the impersonation breadcrumb
 * (`IMPERSONATION_NAME_KEY`) live in localStorage, which is shared across
 * same-origin tabs. This helper reads them synchronously: a storefront tab
 * (Tab B) detects that a privileged session is present in the origin
 * (established by Tab A's back-office login / impersonation) and refuses
 * the swap, rather than clobbering it. A back-office user previewing or
 * opening their OWN store -- whether in an iframe, the same tab, or a
 * second tab -- therefore keeps their session and renders the store
 * read-only.
 *
 * Signals => privileged (skip the swap):
 *   - the impersonation breadcrumb is present (IMPERSONATION_NAME_KEY). Set
 *     by stores/impersonation.startImpersonation, cleared on stop/logout;
 *     requireToken.js already treats it as the synchronous "am I in an
 *     impersonation session?" signal, so we reuse the same source of truth.
 *   - roleName is present AND is not the Shopper role. Vuex login mirrors
 *     role.name into roleName; it is the current canonical synchronous role
 *     signal and survives reload.
 * => NOT privileged (allow the swap):
 *   - roleName === 'Shopper' (a real shopper needs the swap), or
 *   - roleName is absent entirely (anonymous visitor -- they legitimately
 *     need the shopper swap to shop). "No roleName" is NOT privileged.
 *
 * KNOWN LIMITATION (honest scope). Because the signals are localStorage
 * flags shared across tabs, this protects any session that carries a
 * non-Shopper roleName or the impersonation breadcrumb -- which covers the
 * reported case: a back-office user previewing or opening their own store.
 * It does NOT protect a session that has no role signal at all against a
 * separate shopper tab. That broader per-session isolation (namespaced /
 * per-tab tokens) is deferred to post-beta work.
 *
 * SSR / jsdom safety: a storage object without getItem degrades to "not
 * privileged" (never throws), so anonymous shoppers are unaffected.
 */

import { IMPERSONATION_NAME_KEY } from "@/stores/impersonation";

export const ROLE_NAME_KEY = "roleName";
export const SHOPPER_ROLE_NAME = "Shopper";

function readLocal(key) {
  try {
    if (typeof localStorage?.getItem === "function") {
      return localStorage.getItem(key);
    }
  } catch {
    // storage unavailable -> treat as absent
  }
  return null;
}

/**
 * True when the CURRENT origin session must NOT be swapped to a Shopper
 * token -- i.e. an impersonation session, or any logged-in non-Shopper
 * role. Returns false (allow the swap) for a real Shopper session and for
 * an anonymous visitor with no role -- both legitimately need the shopper
 * token swap. Used to route a privileged session into the read-only
 * preview path.
 */
export function isPrivilegedSession() {
  // Impersonation is always privileged regardless of the mirrored role.
  if (readLocal(IMPERSONATION_NAME_KEY)) return true;
  const roleName = readLocal(ROLE_NAME_KEY);
  // Anonymous (no roleName) MUST still swap -> not privileged.
  if (!roleName) return false;
  // A real Shopper MUST still swap -> not privileged. Any other present
  // role is a back-office user -> privileged.
  return roleName !== SHOPPER_ROLE_NAME;
}

export default isPrivilegedSession;
